import { ChangeEvent, useEffect, useRef, useState } from "react";

interface UseTypedSignatureParams {
    resumeDrawing: () => void;
    signatureName: string;
    writeTypedName: (name: string) => Promise<void>;
}

/**
 * Alternative to drawing the signature (RGAA 7.3, WCAG 2.5.7): the user types
 * their name and the pad writes it in the canvas, as the signature to send.
 */
export const useTypedSignature = ({
    resumeDrawing,
    signatureName,
    writeTypedName,
}: UseTypedSignatureParams) => {
    const [isTyping, setIsTyping] = useState(false);
    const [typedName, setTypedName] = useState("");
    const [hasNameError, setHasNameError] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);

    // The field appears where the user did not click: bring them to it
    useEffect(() => {
        if (isTyping) {
            nameInputRef.current?.focus();
        }
    }, [isTyping]);

    function updateName(name: string) {
        setTypedName(name);
        setHasNameError(false);
        writeTypedName(name);
    }

    function startTyping() {
        // Prefilled with the known name: the user only checks or corrects it
        updateName(signatureName);
        setIsTyping(true);
    }

    function stopTyping() {
        setTypedName("");
        setHasNameError(false);
        resumeDrawing();
        setIsTyping(false);
    }

    function handleNameChange(event: ChangeEvent<HTMLInputElement>) {
        updateName(event.target.value);
    }

    function clearName() {
        updateName("");
        nameInputRef.current?.focus();
    }

    // Flags the field and brings the user back to it when the name is missing
    function validateName(): boolean {
        if (typedName.trim()) {
            return true;
        }

        setHasNameError(true);
        nameInputRef.current?.focus();
        return false;
    }

    return {
        clearName,
        handleNameChange,
        hasNameError,
        isTyping,
        nameInputRef,
        startTyping,
        stopTyping,
        typedName,
        validateName,
    };
};
